import { Component, lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CommandPalette } from './CommandPalette';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ChevronsUpDown,
  Clapperboard,
  HardDrive,
  LayoutGrid,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Languages,
  RefreshCw,
  LogOut,
  QrCode,
  Radio,
  ScrollText,
  Search,
  Settings,
  UserRound,
  Users,
} from 'lucide-react';
import { useT, useLang } from '../lib/i18n';
import { useSession, toast } from '../lib/session';
import { useLivePolling, useOverview } from '../lib/live';
import { useHttpRtt } from '../lib/net';
import { greetingKey, shouldAnimate } from '../lib/format';
import { api, avatarUrl } from '../lib/api';
import { useRoomSync } from '../sync/roomStore';
import { Avatar, BrandMark, Dot, Modal, Toggle } from '../ui';
import { AddDevice, DeviceBanner } from '../features/devices/Devices';
import { gsap, useGSAP } from '../motion/gsap';
import { useActiveRoom } from '../features/room/session';
import { RoomInvitePopup } from '../features/room/RoomInvitePopup';
import { NotificationBell } from './NotificationPanel';
import { RecTimer } from '../features/net/RecTimer';
import { PasskeyNudge } from '../features/auth/Passkeys';
import { applyUpdate, useUpdate } from '../lib/install';
import { useCaptureProbes } from '../features/net/captureProbes';
import { syncPushSubscription } from '../lib/push';

const COLLAPSE_KEY = 'vb.sidebar';
// The joined room (sync, voice, the film) lives here, above the pages, so it survives navigation.
// Loaded only once someone joins: it pulls in hls.js and the WebRTC stack.
const RoomSession = lazy(() => import('../features/room/RoomSession'));

function useCollapsed(): [boolean, (v: boolean) => void] {
  const [c, setC] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  return [
    c,
    (v) => {
      setC(v);
      try {
        localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0');
      } catch {
        /* ignore */
      }
    },
  ];
}

function Sidebar({ collapsed, onToggle, onNavigate, phone, drawerOpen }: { collapsed: boolean; onToggle: () => void; onNavigate: () => void; phone: boolean; drawerOpen: boolean }) {
  const t = useT();
  const ov = useOverview((s) => s.data);
  const me = useSession((s) => s.me);
  const user = useSession((s) => s.user);
  const config = useSession((s) => s.config);
  const { pathname } = useLocation();
  const aside = useRef<HTMLElement>(null);
  const indicator = useRef<HTMLDivElement>(null);
  useNavIndicator(aside, indicator, pathname, collapsed, !!user?.host);
  const [tip, setTip] = useState<{ text: string; y: number } | null>(null);

  const items = [
    { to: '/', icon: <LayoutGrid size={17} />, label: t('nav.overview'), end: true },
    { to: '/rooms', icon: <Clapperboard size={17} />, label: t('nav.rooms'), count: ov?.live?.length ? `${ov.live.length} live` : undefined },
    { to: '/drive', icon: <HardDrive size={17} />, label: t('nav.drive'), count: ov?.library.total },
  ];
  const workspace = [
    { to: '/team', icon: <Users size={17} />, label: t('nav.team'), count: me?.team?.members ?? ov?.members },
    { to: '/settings', icon: <Settings size={17} />, label: t('nav.settings') },
    ...(user?.host ? [{ to: '/logs', icon: <ScrollText size={17} />, label: t('nav.logs') }] : []),
  ];
  const tipProps = (label: string) =>
    collapsed
      ? {
          onMouseEnter: (e: React.MouseEvent) => setTip({ text: label, y: (e.currentTarget as HTMLElement).getBoundingClientRect().top + 10 }),
          onMouseLeave: () => setTip(null),
        }
      : {};

  const link = (i: { to: string; icon: React.ReactNode; label: string; end?: boolean; count?: number | string }) => (
    <NavLink key={i.to} to={i.to} end={i.end} onClick={onNavigate} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} {...tipProps(i.label)}>
      {i.icon}
      <span className="nav-text">{i.label}</span>
      {i.count !== undefined && <span className="count">{i.count}</span>}
    </NavLink>
  );

  return (
    <aside ref={aside} className="sidebar" aria-hidden={phone && !drawerOpen} inert={phone && !drawerOpen}>
      <div className="brand">
        <BrandMark />
        <span className="brand-name">Video Bridge</span>
        <button className="icon-btn" onClick={onToggle} aria-label={collapsed ? t('nav.expand') : t('nav.collapse')} {...tipProps(t('nav.expand'))}>
          {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
      </div>
      <div className="workspace" {...tipProps(config?.serverLabel ?? '')}>
        <div className="workspace-icon">
          <HardDrive size={15} />
        </div>
        <div className="workspace-text" style={{ minWidth: 0 }}>
          <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{config?.serverLabel || 'Video Bridge'}</b>
          <span>{ov?.drive.name ?? ''}</span>
        </div>
      </div>
      <div ref={indicator} className="nav-indicator" aria-hidden />
      <nav style={{ display: 'contents' }}>
        {items.map(link)}
        <div className="nav-label">{t('nav.workspace')}</div>
        {workspace.map(link)}
      </nav>
      <LangSwitch collapsed={collapsed} />
      <ConnectionCard collapsed={collapsed} onNavigate={onNavigate} tipProps={tipProps} />
      {tip && createPortal(<RailTip text={tip.text} y={tip.y} />, document.body)}
    </aside>
  );
}

/** One black pill slides between nav items instead of each item switching its own background. */
function useNavIndicator(aside: React.RefObject<HTMLElement | null>, indicator: React.RefObject<HTMLDivElement | null>, pathname: string, collapsed: boolean, host: boolean) {
  const placed = useRef(false);
  useEffect(() => {
    const place = (animate: boolean) => {
      // Find the target from the path, not the .active class, which can lag a render behind.
      const links = [...(aside.current?.querySelectorAll<HTMLAnchorElement>('a.nav-item') ?? [])];
      const href = (a: HTMLAnchorElement) => a.getAttribute('href') ?? '';
      const el =
        links.find((a) => href(a) === pathname) ??
        links.filter((a) => href(a) !== '/' && pathname.startsWith(href(a) + '/')).sort((a, b) => href(b).length - href(a).length)[0];
      const ind = indicator.current;
      if (!ind) return;
      if (!el) return void gsap.set(ind, { opacity: 0 });
      const to = { x: el.offsetLeft, y: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight, opacity: 1 };
      if (animate && placed.current && shouldAnimate()) {
        gsap.to(ind, { ...to, duration: 0.42, ease: 'power3.out', overwrite: true });
        slideUntil = performance.now() + 420;
      } else gsap.set(ind, to);
      placed.current = true;
    };
    // gsap.isTweening() is false for a tween created this tick, so track the slide ourselves.
    let slideUntil = 0;
    const sliding = () => performance.now() < slideUntil;
    place(true);
    // Items move when counts, the workspace card or web fonts load after the first paint, so
    // re-measure whenever anything in the sidebar changes size.
    // A ResizeObserver also fires once when it starts observing; while the pill is sliding, retarget
    // the slide instead of snapping, or that first callback cancels the animation.
    const ro = new ResizeObserver(() => place(sliding()));
    if (aside.current) {
      ro.observe(aside.current);
      aside.current.querySelectorAll(':scope > *, .nav-item').forEach((n) => ro.observe(n));
    }
    let alive = true;
    document.fonts?.ready.then(() => alive && place(sliding()));
    return () => {
      alive = false;
      ro.disconnect();
    };
  }, [aside, indicator, pathname, collapsed, host]);
}

/** The sidebar's connection card: sync transport, server round trip, drive state, people watching. */
function ConnectionCard({ collapsed, onNavigate, tipProps }: { collapsed: boolean; onNavigate: () => void; tipProps: (label: string) => object }) {
  const syncTransport = useRoomSync((s) => s.transport);
  const syncRtt = useRoomSync((s) => s.rtt);
  const httpRtt = useHttpRtt();
  const rtt = syncRtt || httpRtt;
  const rttText = rtt ? `${Math.round(rtt)} ms` : '—';
  return (
    <div className="side-card" {...tipProps(`${syncTransport === 'websocket' ? 'WebSocket' : 'WebTransport'} · ${rttText}`)}>
      {collapsed ? (
        <div className="side-card-icon">
          <Radio size={15} className="accent-ic" />
        </div>
      ) : (
        <ConnectionDetails rttText={rttText} onNavigate={onNavigate} />
      )}
    </div>
  );
}

function ConnectionDetails({ rttText, onNavigate }: { rttText: string; onNavigate: () => void }) {
  const t = useT();
  const ov = useOverview((s) => s.data);
  const pref = useSession((s) => s.transportPref);
  const setPref = useSession((s) => s.setTransportPref);
  const syncTransport = useRoomSync((s) => s.transport);
  const tcp = syncTransport === 'websocket' || pref === 'websocket';
  const driveOffline = ov?.drive.online === false;
  return (
    <div className="side-card-body" style={{ display: 'block' }}>
      <div className="side-card-head">
        <div className="side-card-icon">
          <Radio size={15} className="accent-ic" />
        </div>
        <div style={{ flex: 1 }}>
          <b>{t('conn.title')}</b>
          <small>{tcp ? 'WebSocket / TCP' : 'WebTransport / QUIC'}</small>
        </div>
        <Toggle on={pref !== 'websocket'} onChange={(v) => setPref(v ? 'auto' : 'websocket')} label={t('conn.prefer')} />
      </div>
      <div className="side-rows">
        <div>
          <Dot tone={rttText === '—' ? 'gray' : 'green'} />
          <span>{t('conn.server')}</span>
          <span className="mono">{rttText}</span>
        </div>
        <div>
          <Dot tone={driveOffline ? 'red' : 'green'} />
          <span>{t('nav.drive')}</span>
          <span className="mono">{driveOffline ? 'offline' : 'online'}</span>
        </div>
      </div>
      <NavLink to="/rooms" className="side-pill" onClick={onNavigate}>
        {t('chip.watching', { n: ov?.online ?? 0 })}
        <ChevronRight size={14} />
      </NavLink>
    </div>
  );
}

function RailTip({ text, y }: { text: string; y: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current && shouldAnimate()) gsap.fromTo(ref.current, { x: -6, opacity: 0 }, { x: 0, opacity: 1, duration: 0.18, ease: 'power2.out' });
  }, []);
  return (
    <div ref={ref} className="rail-tip" style={{ left: 100, top: y }}>
      {text}
    </div>
  );
}

/** English / 日本語, in the sidebar (the one place to change it besides Settings). */
function LangSwitch({ collapsed }: { collapsed: boolean }) {
  const { lang, setLang } = useLang();
  const change = (l: 'en' | 'ja') => {
    setLang(l);
    api.updateProfile({ lang: l }).catch(() => undefined);
  };
  if (collapsed)
    return (
      <button className="nav-item lang-rail" onClick={() => change(lang === 'en' ? 'ja' : 'en')} aria-label="Language" title={lang === 'en' ? '日本語' : 'English'}>
        <Languages size={17} />
      </button>
    );
  return (
    <div className="lang side-lang" role="group" aria-label="Language">
      <Languages size={15} className="muted" />
      <button className={lang === 'en' ? 'on' : ''} onClick={() => change('en')}>
        English
      </button>
      <button className={lang === 'ja' ? 'on' : ''} onClick={() => change('ja')}>
        日本語
      </button>
    </div>
  );
}

/**
 * A page that can't load (its file is from a newer release than this open page) shows an Update
 * card instead of taking the whole app, and the room you're in, down with it.
 */
class PartBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <section className="card" style={{ maxWidth: 520 }}>
        <h2 className="card-title">A newer version is ready</h2>
        <p className="app-lead">This page changed in the latest update. Update to open it (you'll rejoin your room right after).</p>
        <button className="btn btn-black" onClick={applyUpdate}>
          <RefreshCw size={14} /> Update
        </button>
      </section>
    );
  }
}

function Header({ onMenu, onSearch }: { onMenu: () => void; onSearch: () => void }) {
  const t = useT();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const user = useSession((s) => s.user);
  const ov = useOverview((s) => s.data);
  const updateReady = useUpdate((s) => s.ready);
  const chipRef = useRef<HTMLSpanElement>(null);
  const processing = ov ? ov.pipeline.queued + ov.pipeline.probing + ov.pipeline.remuxing + ov.pipeline.transcoding : 0;
  const chip = ov?.online ? t('chip.watching', { n: ov.online }) : processing ? t('chip.processing', { n: processing }) : t('chip.allReady');

  useGSAP(() => {
    if (chipRef.current && shouldAnimate()) gsap.from(chipRef.current, { scale: 0.85, opacity: 0, duration: 0.5, ease: 'back.out(2)' });
  }, [chip]);

  return (
    <header className="header">
      <button className="round-btn menu-btn" onClick={onMenu} aria-label="Menu">
        <Menu size={17} />
      </button>
      <h1 className="greeting">
        {t(`greet.${greetingKey()}`)}, {user?.name.split(' ')[0]}
      </h1>
      {!pathname.startsWith('/room/') && (
        <span ref={chipRef} className={`chip chip-accent header-chip${!ov?.online && !processing ? ' idle' : ''}`}>
          <Dot tone={ov?.online ? 'green' : 'gray'} />
          {chip}
        </span>
      )}
      <div className="header-spacer" />
      <button className="search" type="button" onClick={onSearch} aria-label={t('search.placeholder')}>
        <Search size={15} />
        <span className="search-text">{t('search.placeholder')}</span>
        <span className="kbd">⌘K</span>
      </button>
      {ov?.capture && (
        <button className="chip rec-chip" onClick={() => nav('/settings?tab=network')} title="A network capture is recording (Settings → Network)">
          <span className="rec-dot" /> Recording network <RecTimer since={ov.capture.since} />
        </button>
      )}
      {updateReady && (
        <button className="btn btn-accent btn-sm update-btn" onClick={applyUpdate} title="A new version of Video Bridge is ready">
          <RefreshCw size={13} /> Update
        </button>
      )}
      <AccountMenu />
      <NotificationBell />
    </header>
  );
}

/** The name pill: who is signed in, with profile and sign out. */
function AccountMenu() {
  const t = useT();
  const nav = useNavigate();
  const { user, signOut } = useSession();
  const [open, setOpen] = useState(false);
  const [addDevice, setAddDevice] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div className="account" ref={box}>
      <button className="user-pill" onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open}>
        <Avatar name={user?.name ?? '?'} src={user ? avatarUrl(user) : undefined} />
        <span>{user?.name}</span>
        <ChevronsUpDown size={14} className="muted" />
      </button>
      {open && (
        <div className="menu account-menu" role="menu">
          <div className="account-who">
            <b>{user?.name}</b>
            <span>{user?.email}</span>
            {user?.publicId && <span className="mono">{user.publicId}</span>}
          </div>
          <button
            onClick={() => {
              setOpen(false);
              nav('/settings');
            }}
          >
            <UserRound size={14} /> Profile &amp; settings
          </button>
          <button
            onClick={() => {
              setOpen(false);
              setAddDevice(true);
            }}
          >
            <QrCode size={14} /> Sign in on another device
          </button>
          <button
            className="danger"
            onClick={async () => {
              setOpen(false);
              await signOut();
              nav('/login');
            }}
          >
            <LogOut size={14} /> {t('btn.signOut')}
          </button>
        </div>
      )}
      <Modal open={addDevice} onClose={() => setAddDevice(false)}>
        {addDevice && <AddDevice />}
      </Modal>
    </div>
  );
}

function Banners() {
  const t = useT();
  const user = useSession((s) => s.user);
  const ov = useOverview((s) => s.data);
  const [sent, setSent] = useState(false);
  return (
    <>
      {ov?.drive.online === false && (
        <div className="banner danger" style={{ marginBottom: 16 }}>
          <HardDrive size={16} />
          {t('drive.offline')}
        </div>
      )}
      {user && !user.emailVerified && (
        <div className="banner" style={{ marginBottom: 16 }}>
          {t('auth.verifyBanner', { email: user.email })}
          <span className="spacer" />
          <button
            className="btn btn-black btn-sm"
            disabled={sent}
            onClick={() =>
              api
                .resendVerification()
                .then((r) => {
                  setSent(true);
                  toast(r.emailEnabled ? 'Sent — check your inbox' : 'Email isn’t set up on this server — the host can confirm you in Team', 'accent');
                })
                .catch((e) => toast((e as Error).message, 'red'))
            }
          >
            {t('auth.resend')}
          </button>
        </div>
      )}
    </>
  );
}

function useIsPhone() {
  const q = '(max-width: 760px)';
  const [phone, setPhone] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}

export function AppShell() {
  const [palette, setPalette] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  // Soft cross-fade whenever the route changes.
  useEffect(() => {
    if (mainRef.current && shouldAnimate()) gsap.fromTo(mainRef.current, { opacity: 0.4, y: 8 }, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', clearProps: 'transform,opacity' });
  }, [pathname]);
  useLivePolling();
  const [savedCollapsed, setCollapsed] = useCollapsed();
  const phone = useIsPhone();
  // On phones the sidebar is a full drawer, never the icon rail.
  const collapsed = savedCollapsed && !phone;
  const [drawer, setDrawer] = useState(false);
  useCaptureProbes();
  // Keep this device's notification subscription known to the server.
  useEffect(() => {
    syncPushSubscription().catch(() => undefined);
  }, []);
  const activeRoom = useActiveRoom((s) => s.roomId);
  useEffect(() => {
    if (!activeRoom && useUpdate.getState().ready) applyUpdate();
  }, [activeRoom]);
  const shell = useRef<HTMLDivElement>(null);
  const loc = useLocation();

  // Navigating closes the phone drawer; done while rendering so the new page never shows under an open drawer.
  const [drawerPath, setDrawerPath] = useState(loc.pathname);
  if (drawerPath !== loc.pathname) {
    setDrawerPath(loc.pathname);
    setDrawer(false);
  }
  // Phone drawer: the panel slides (CSS); its contents follow a beat later.
  useEffect(() => {
    if (!phone || !drawer || !shouldAnimate()) return;
    const items = shell.current?.querySelectorAll('.sidebar > *:not(.nav-indicator), .sidebar .nav-item');
    if (items?.length) gsap.fromTo(items, { x: -16, opacity: 0 }, { x: 0, opacity: 1, duration: 0.4, stagger: 0.025, delay: 0.08, ease: 'power3.out', clearProps: 'transform,opacity' });
  }, [phone, drawer]);
  useEffect(() => {
    if (!phone || !drawer) return;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawer(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [phone, drawer]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette((o) => !o);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Animated collapse: fade the labels, then slide the rail (and the reverse on expand).
  const toggle = () => {
    const el = shell.current;
    if (!el || phone) return setDrawer((d) => !d);
    const next = !collapsed;
    if (!shouldAnimate()) return setCollapsed(next);
    const labels = el.querySelectorAll('.nav-text, .brand-name, .workspace-text, .count, .nav-label, .side-card-body');
    if (next) {
      gsap
        .timeline()
        .to(labels, { opacity: 0, duration: 0.15, ease: 'power1.in' })
        .to(el, { '--sidebar-w': '76px', duration: 0.38, ease: 'power3.inOut' })
        .add(() => {
          setCollapsed(true);
          gsap.set(labels, { clearProps: 'opacity' });
        });
    } else {
      setCollapsed(false);
      requestAnimationFrame(() => {
        const fresh = el.querySelectorAll('.nav-text, .brand-name, .workspace-text, .count, .nav-label, .side-card-body');
        gsap
          .timeline()
          .fromTo(el, { '--sidebar-w': '76px' }, { '--sidebar-w': '240px', duration: 0.38, ease: 'power3.inOut' })
          .fromTo(fresh, { opacity: 0 }, { opacity: 1, duration: 0.2, clearProps: 'opacity' });
      });
    }
  };

  useEffect(() => {
    shell.current?.style.setProperty('--sidebar-w', collapsed ? '76px' : '240px');
  }, [collapsed]);

  return (
    <div ref={shell} className={`shell${collapsed ? ' collapsed' : ''}${drawer ? ' drawer-open' : ''}`}>
      <Sidebar collapsed={collapsed} onToggle={toggle} onNavigate={() => setDrawer(false)} phone={phone} drawerOpen={drawer} />
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
      {/* A pointer shortcut only: keyboard users close the drawer with Escape. */}
      <div className="drawer-scrim" role="presentation" onClick={() => setDrawer(false)} />
      <main ref={mainRef}>
        <Header onMenu={() => setDrawer(true)} onSearch={() => setPalette(true)} />
        <Banners />
        <PasskeyNudge />
        <DeviceBanner />
        {/* Pages that load on demand keep the sidebar and header on screen while they arrive. */}
        <PartBoundary key={loc.pathname}>
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </PartBoundary>
      </main>
      <RoomInvitePopup />
      {activeRoom && (
        <Suspense fallback={null}>
          <RoomSession key={activeRoom} roomId={activeRoom} />
        </Suspense>
      )}
    </div>
  );
}
